/**
 * FactoringReportTab — TES-04: Reporte de Operaciones de Factoring
 */
import { todayISO } from '../../../lib/utils'
import { useState } from 'react'
import DatePicker from '@/components/DatePicker'
import { fmtDate, formatRut } from '@/lib/utils'
import { useTenantLocale } from '@/lib/locale'
import { BarChart3, Loader2 } from 'lucide-react'
import { DataTable } from '@/components/ds/DataTable'
import { useFactoringReport, useFactoringEntities, type FactoringOperationStatus } from '../hooks/useTreasury'
import { EmptyState } from '@/components/EmptyState'

const STATUS_LABELS: Record<FactoringOperationStatus, string> = {
  created:    'Creada',
  processing: 'Procesando',
  accepted:   'Aceptada',
  rejected:   'Rechazada',
  paid:       'Liquidada',
}

function fmtNum(value: string | undefined) {
  if (!value) return '—'
  const n = parseFloat(value)
  if (isNaN(n)) return value
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 })
}

export function FactoringReportTab() {
  const locale = useTenantLocale()
  const today = todayISO()
  const firstOfMonth = today.substring(0, 7) + '-01'

  const [dateFrom, setDateFrom] = useState(firstOfMonth)
  const [dateTo, setDateTo] = useState(today)
  const [entityId, setEntityId] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [generated, setGenerated] = useState(false)

  const { data: entitiesData } = useFactoringEntities()
  const entities = entitiesData?.entities ?? []

  const reportParams = generated
    ? {
        date_from: dateFrom,
        date_to: dateTo,
        ...(entityId ? { entity_id: entityId } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      }
    : null

  const { data, isLoading } = useFactoringReport(reportParams)

  return (
    <div>
      {/* Filters */}
      <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 mb-6 shadow-card">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="factoringreporttab-fecha-desde" className="block text-xs font-medium text-content-muted mb-1">Fecha Desde</label>
            <DatePicker id="factoringreporttab-fecha-desde" value={dateFrom} onChange={v => { setDateFrom(v); setGenerated(false) }} />
          </div>
          <div>
            <label htmlFor="factoringreporttab-fecha-hasta" className="block text-xs font-medium text-content-muted mb-1">Fecha Hasta</label>
            <DatePicker id="factoringreporttab-fecha-hasta" value={dateTo} onChange={v => { setDateTo(v); setGenerated(false) }} />
          </div>
          <div>
            <label htmlFor="factoringreporttab-entidad" className="block text-xs font-medium text-content-muted mb-1">Entidad</label>
            <select id="factoringreporttab-entidad"
              value={entityId}
              onChange={e => { setEntityId(e.target.value); setGenerated(false) }}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="">Todas las entidades</option>
              {entities.map(e => (
                <option key={e.id} value={e.id}>{e.legal_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="factoringreporttab-estado" className="block text-xs font-medium text-content-muted mb-1">Estado</label>
            <select id="factoringreporttab-estado"
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setGenerated(false) }}
              className="w-full border border-divider rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
            >
              <option value="">Todos los estados</option>
              <option value="created">Creada</option>
              <option value="processing">Procesando</option>
              <option value="accepted">Aceptada</option>
              <option value="rejected">Rechazada</option>
              <option value="paid">Liquidada</option>
            </select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={() => setGenerated(true)}
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm rounded-lg hover:bg-primary-hover"
          >
            {isLoading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
            Generar Reporte
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16 text-content-subtle">
          <Loader2 size={20} className="animate-spin mr-2" />Generando...
        </div>
      )}

      {!isLoading && data && (
        <div>
          {/* Totals */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
              <p className="text-xs text-content-muted mb-1">Operaciones</p>
              <p className="text-2xl font-bold text-content">{data.count}</p>
            </div>
            <div className="bg-glass backdrop-blur-glass rounded-card border border-divider p-4 shadow-card">
              <p className="text-xs text-content-muted mb-1">Total Cedido</p>
              <p className="text-2xl font-bold text-content">{fmtNum(data.total_amount_ceded)}</p>
            </div>
            <div className="bg-success-surface rounded-xl border border-success-border p-4">
              <p className="text-xs text-success-content-soft mb-1">Total Liquidado</p>
              <p className="text-2xl font-bold text-success-content">{fmtNum(data.total_amount_paid)}</p>
            </div>
          </div>

          {/* Table */}
          {data.count === 0 ? (
            <EmptyState title="No se encontraron operaciones en el período." size="sm" />
          ) : (
            <DataTable
              density="cozy"
              columns={[
                { key: 'date', header: 'Fecha', render: r => fmtDate(r.date, locale) },
                { key: 'entity_name', header: 'Entidad', render: r => <span className="font-medium text-content">{r.entity_name}</span> },
                { key: 'entity_tax_id', header: 'RUT', render: r => <span className="font-mono">{formatRut(r.entity_tax_id)}</span> },
                { key: 'line_count', header: 'Docs', numeric: true },
                {
                  key: 'total_amount_ceded', header: 'Total Cedido', numeric: true,
                  render: r => <span className="font-medium text-content">{fmtNum(r.total_amount_ceded)}</span>,
                },
                { key: 'status', header: 'Estado', render: r => STATUS_LABELS[r.status] },
                { key: 'payment_date', header: 'F. Pago', numeric: true, render: r => fmtDate(r.payment?.payment_date, locale) },
                {
                  key: 'amount_received_net', header: 'Monto Neto', numeric: true,
                  render: r => (
                    <span className="font-medium text-success-content">
                      {r.payment ? fmtNum(r.payment.amount_received_net) : '—'}
                    </span>
                  ),
                },
              ]}
              rows={data.operations}
              rowKey={r => r.id}
              minWidth={980}
              caption="Operaciones de factoring del período"
            />
          )}
        </div>
      )}
    </div>
  )
}
